import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { Button, FlagRow, Panel, RoboAnimations, Select, Stat, StatusPill } from "./ui";
import AssetsPanel from "./AssetsPanel";
import ReviewPanel from "./ReviewPanel";

// The PDF ingestion workflow.
//
// All meaningful state lives on the server: this component holds a paper id and
// re-reads the paper after every action. That is what lets an admin refresh the tab,
// close it, and come back to a half-finished review without losing the analysis.

const PAPERS = ["1", "2", "3"];
const YEARS = Array.from({ length: 20 }, (_, i) => String(2026 - i));

const STAGES = [
  ["uploaded",              "Upload"],
  ["analyzed",              "Analyse"],
  ["assets_ready",          "Assets"],
  ["machine_reviewing",     "Solve"],
  ["awaiting_human_review", "Machine QA"],
  ["json_built",            "Review & build"],
  ["imported",              "Import"],
  ["completed",             "Complete"],
];

const STAGE_ORDER = STAGES.map(([key]) => key);

// What each background stage is doing, in words rather than an enum name.
const RUNNING_LABEL = {
  analyzing:          "Reading the paper",
  extracting_assets:  "Cropping images",
  solving:            "Generating answers",
  reviewing:          "Running machine QA",
};

// While a stage runs the paper sits on the stage BEFORE the one being worked on, so
// the rail would otherwise show no sign that anything is happening. This maps a
// running stage to the rail position it is working towards.
const RUNNING_TARGET = {
  analyzing:         "analyzed",
  extracting_assets: "assets_ready",
  solving:           "machine_reviewing",
  reviewing:         "awaiting_human_review",
};

const StageRail = ({ stage, running }) => {
  const target  = running ? RUNNING_TARGET[stage] : null;
  const working = target ? STAGE_ORDER.indexOf(target) : -1;
  const current = STAGE_ORDER.indexOf(stage);

  return (
    <ol className="flex flex-wrap items-center gap-1.5">
      {STAGES.map(([key, label], index) => {
        const inFlight = working === index;
        const done     = current > index || (working > index);
        const active   = current === index && !inFlight;
        return (
          <li key={key} className="flex items-center gap-1.5">
            <span className={`relative overflow-hidden rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
              inFlight ? "robo-sweep robo-glow bg-[#2e92b0] text-white"
              : active ? "bg-[#2e92b0] text-white"
              : done   ? "bg-emerald-500/15 text-emerald-300"
              : "bg-white/5 text-white/35"
            }`}>
              {label}
            </span>
            {index < STAGES.length - 1 && (
              <span className={index < Math.max(current, working) ? "text-emerald-400/40" : "text-white/15"}>›</span>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const VerificationPanel = ({ paper, onChanged }) => {
  const [overriding, setOverriding] = useState(false);
  const { verification } = paper;

  const tone = verification.cleared ? "green"
    : verification.status === "mismatch" ? "red" : "amber";

  const label = verification.overridden_at ? "Overridden by an admin"
    : verification.status === "verified" ? "Identity verified"
    : verification.status === "mismatch" ? "Identity mismatch"
    : "Could not verify identity";

  const override = async () => {
    setOverriding(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/admin/robo/papers/${paper.id}/override_verification`
      );
      if (data.success) onChanged(data.paper);
    } finally {
      setOverriding(false);
    }
  };

  return (
    <Panel title="Paper Verification" action={<StatusPill color={tone}>{label}</StatusPill>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-white/35">You selected</p>
          <p className="mt-1 text-sm text-white">{verification.expected}</p>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wider text-white/35">The PDF says</p>
          <p className="mt-1 text-sm text-white">{verification.observed || "—"}</p>
        </div>
      </div>

      {(verification.differences || []).length > 0 && (
        <ul className="mt-3 space-y-1">
          {verification.differences.map((d) => (
            <li key={d.field} className="text-xs text-red-300">
              {d.field}: expected <span className="font-semibold">{d.expected}</span>,
              {" "}found <span className="font-semibold">{d.observed || "nothing"}</span>
            </li>
          ))}
        </ul>
      )}

      {!verification.cleared && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="danger" disabled={overriding} onClick={override}>
            Override And Continue Anyway
          </Button>
          <span className="text-[11px] text-white/40">
            Nothing else runs until this is resolved. Overriding is recorded.
          </span>
        </div>
      )}
    </Panel>
  );
};

const SummaryPanel = ({ paper }) => {
  const s = paper.summary;
  return (
    <Panel
      title={`${paper.exam_type} ${paper.subject} · ${paper.year} · Paper ${paper.paper}`}
      subtitle={[
        paper.original_filename,
        paper.page_count ? `${paper.page_count} pages` : null,
        paper.provenance.model,
        paper.provenance.parser_file ? `parser ${paper.provenance.parser_checksum}` : null,
      ].filter(Boolean).join(" · ")}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Top-level questions" value={s.top_level_questions} />
        <Stat label="Answerable parts" value={s.answerable_parts} />
        <Stat label="Need a visual" value={s.questions_needing_assets} />
        <Stat label="Assets detected" value={s.assets_detected} />
        <Stat label="Extracted" value={s.assets_extracted} tone={s.assets_extracted === s.assets_detected ? "green" : "amber"} />
        <Stat label="Crops verified" value={s.assets_verified} tone={s.assets_verified === s.assets_detected ? "green" : "amber"} />
        <Stat label="Answers generated" value={s.answers_generated} />
        <Stat label="No machine concerns" value={s.machine_no_concerns} tone="green" />
        <Stat label="Review recommended" value={s.machine_review_recommended} tone="amber" />
        <Stat label="Likely issue" value={s.machine_likely_issue} tone="red" />
        <Stat label="Human approved" value={`${s.human_approved}/${s.answerable_parts}`} tone={s.human_pending === 0 ? "green" : "slate"} />
        <Stat label="Critical flags" value={s.flags_critical} tone={s.flags_critical ? "red" : "green"} />
      </div>

      {s.questions_needing_assets_list?.length > 0 && (
        <p className="mt-3 text-[11px] text-white/45">
          Questions needing a visual:{" "}
          <span className="text-white/70">
            {s.questions_needing_assets_list.map((n) => `Q${n}`).join(", ")}
          </span>
        </p>
      )}
    </Panel>
  );
};

const FinalizePanel = ({ paper, onChanged, highlight }) => {
  const [busy, setBusy] = useState("");
  const [blockers, setBlockers] = useState([]);
  const [message, setMessage] = useState("");

  const run = async (action, label) => {
    setBusy(label);
    setBlockers([]);
    setMessage("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/robo/papers/${paper.id}/${action}`);
      if (!data.success) {
        setBlockers(data.blockers || [data.error]);
      } else {
        onChanged(data.paper);
        if (data.build)  setMessage(`Wrote ${data.build.questions_written} question(s) to ${data.build.json_path}`);
        if (data.import) setMessage(`Imported ${data.import.imported}, skipped ${data.import.skipped}`);
        if (action === "finalize") setMessage("Paper finalised. Staging data cleared.");
      }
    } catch (e) {
      setBlockers(e.response?.data?.blockers || [e.response?.data?.error || e.message]);
    } finally {
      setBusy("");
    }
  };

  // Each publish step has a prerequisite the backend already enforces. Mirroring it
  // here means the button explains itself instead of being clickable and then
  // rejected — the reason is the useful part, so it goes in the tooltip.
  const s = paper.summary;
  const buildBlocked =
    !s.answers_generated ? "Generate answers first."
    : s.human_pending > 0 ? `${s.human_pending} question(s) still need your approval.`
    : null;
  const importBlocked = !paper.json_path ? "Build the JSON first." : null;
  const finalizeBlocked =
    paper.stage === "completed" ? "Already finalised."
    : paper.stage !== "imported" ? "Import to the database first."
    : null;

  return (
    <Panel
      highlight={highlight}
      collapsible
      autoOpen={highlight}
      defaultOpen={highlight}
      title="Publish"
      subtitle="Building JSON does not publish. Importing does. Finalising clears the workbench."
    >
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={!!busy || !!buildBlocked}
          title={buildBlocked || "Write the approved questions to a JSON file"}
          onClick={() => run("build_json", "build")}
        >
          {busy === "build" ? "Building…" : "Build JSON"}
        </Button>
        <Button
          disabled={!!busy || !!importBlocked}
          title={importBlocked || "Insert the questions into the database"}
          onClick={() => run("import", "import")}
        >
          {busy === "import" ? "Importing…" : "Import To DB"}
        </Button>
        <Button
          variant="success"
          disabled={!!busy || !!finalizeBlocked}
          title={finalizeBlocked || "Clear the workbench and keep only the finished paper"}
          onClick={() => run("finalize", "finalize")}
          className="ml-auto"
        >
          {paper.stage === "completed" ? "✓ Finalised" : "✓ Verify Complete & Finalise"}
        </Button>
      </div>

      {(buildBlocked || importBlocked) && !busy && (
        <p className="mt-2 text-[11px] text-white/40">
          {buildBlocked || importBlocked}
        </p>
      )}

      {message && <p className="mt-3 text-xs text-emerald-300">{message}</p>}

      {blockers.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5">
          <p className="mb-1 text-xs font-semibold text-amber-200">Not finished yet</p>
          <ul className="space-y-1">
            {blockers.map((b, i) => (
              <li key={i} className="text-xs leading-relaxed text-amber-100/80">• {b}</li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
};

const PdfPipeline = ({ subjectOptions, onPapersChanged }) => {
  const [subject, setSubject]   = useState("");
  const [examType, setExamType] = useState("BGCSE");
  const [year, setYear]         = useState(YEARS[0]);
  const [paperNo, setPaperNo]   = useState("1");
  const [file, setFile]         = useState(null);

  // The key this paper will be saved under. Mirrors Robo::PaperIdentity.doc_external_id
  // so what is previewed here is exactly what the backend will create.
  const docExternalId =
    `${subject.toLowerCase().replace(/\s+/g, "_")}_${examType.toLowerCase()}_${year}_${paperNo}`;

  const [paper, setPaper]   = useState(null);
  const [busy, setBusy]     = useState("");
  const [error, setError]   = useState("");
  const [recent, setRecent] = useState([]);

  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(async () => {
    if (!paper?.id) return;
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/papers/${paper.id}`);
      if (data.success) setPaper(data.paper);
    } catch { /* leave what is on screen; the button can be pressed again */ }
    finally { setRefreshing(false); }
  }, [paper?.id]);

  // The long stages run as background jobs, so the browser watches the paper rather
  // than holding a request open. Polling stops the moment the stage settles.
  useEffect(() => {
    if (!paper?.running) return undefined;

    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/papers/${paper.id}`);
        if (data.success) setPaper(data.paper);
      } catch { /* a dropped poll is harmless; the next one will catch up */ }
    }, 3000);

    return () => clearInterval(timer);
  }, [paper?.running, paper?.id]);

  // One more read once a stage finishes. The poll that first sees `running: false`
  // can land in the gap between the stage settling and the last of its results being
  // written — which is how a finished refinement showed every crop as "Not checked".
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !paper?.running) reload();
    wasRunning.current = !!paper?.running;
  }, [paper?.running, reload]);

  const subjects = subjectOptions.map((s) => s.subject_name);
  const examTypes = (subjectOptions.find((s) => s.subject_name === subject)?.exam_types || [])
    .map((b) => b.exam_type);
  const hasParser = subjectOptions
    .find((s) => s.subject_name === subject)?.exam_types
    ?.find((b) => b.exam_type === examType)?.has_parser;

  useEffect(() => {
    if (!subject && subjects.length) setSubject(subjects[0]);
  }, [subjects, subject]);

  useEffect(() => {
    if (examTypes.length && !examTypes.includes(examType)) setExamType(examTypes[0]);
  }, [examTypes, examType]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/papers`);
      if (data.success) setRecent(data.papers);
    } catch { /* the list is a convenience, not a requirement */ }
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const open = async (id) => {
    setError("");
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/admin/robo/papers/${id}`);
      if (data.success) setPaper(data.paper);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy("upload");
    setError("");
    try {
      const form = new FormData();
      form.append("subject", subject);
      form.append("examType", examType);
      form.append("year", year);
      form.append("paper", paperNo);
      form.append("pdf", file);
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/robo/papers`, form);
      if (!data.success) throw new Error(data.error);
      setPaper(data.paper);
      setFile(null);
      loadRecent();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy("");
    }
  };

  const act = async (action, body = undefined) => {
    setBusy(action);
    setError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/admin/robo/papers/${paper.id}/${action}`, body);
      if (data.paper) setPaper(data.paper);
      if (!data.success) setError(data.error);
      onPapersChanged?.();
    } catch (e) {
      if (e.response?.data?.paper) setPaper(e.response.data.paper);
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy("");
      loadRecent();
    }
  };

  // ── Picking / uploading ──────────────────────────────────────────────────
  if (!paper) {
    return (
      <div className="space-y-4">
        <Panel
          title="Upload The Complete Exam Paper PDF"
          subtitle="One PDF. No page screenshots needed."
          /* The key this paper will be saved under, shown before upload so a wrong
             selection is caught here rather than after a full parse. Mirrors
             Robo::PaperIdentity.doc_external_id on the backend. */
          action={
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-white/45">doc_external_id:</span>
              <code className="select-all break-all rounded-md border border-[#2e92b0]/30 bg-[#2e92b0]/10 px-2.5 py-1 font-mono text-xs text-[#7fd4ec]">
                {docExternalId}
              </code>
            </div>
          }
        >
          {/* Robo writes its JSON and question images into the repository itself, and
              those files are meant to be committed. A container filesystem is wiped on
              every deploy and cannot commit anything, so running this against production
              would quietly discard the work. */}
          <div className="mb-4 rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3.5 py-2.5">
            <p className="text-xs leading-relaxed text-amber-100/85">
              <span className="font-semibold">Run this locally.</span> Robo writes the paper's
              JSON and images into the repo as files we then commit to git. Run against a
              deployed server and those files land in a container that is wiped on the next
              deploy, so the work would be lost.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Select label="Subject" value={subject} onChange={setSubject} options={subjects} />
            <Select label="Exam type" value={examType} onChange={setExamType} options={examTypes} />
            <Select label="Year" value={year} onChange={setYear} options={YEARS} />
            <Select label="Paper" value={paperNo} onChange={setPaperNo} options={PAPERS} />
          </div>

          {subject && hasParser === false && (
            <p className="mt-3 rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-200">
              No parser instructions exist for {subject} ({examType}). Add a parser MD file before ingesting this paper.
            </p>
          )}

          <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center transition-colors hover:border-[#2e92b0]/50 hover:bg-white/5">
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <span className="text-sm text-white/70">
              {file ? file.name : "Choose a PDF, or drop one here"}
            </span>
            <span className="mt-1 text-[11px] text-white/35">Up to 32 MB</span>
          </label>

          {error && <p className="mt-3 text-xs text-red-300">{error}</p>}

          <div className="mt-4">
            <Button variant="primary" disabled={!file || !subject || busy === "upload" || hasParser === false} onClick={upload}>
              {busy === "upload" ? "Uploading…" : "Upload & Let's Get To Work!"}
            </Button>
          </div>
        </Panel>

        {recent.length > 0 && (
          <Panel title="In Progress" subtitle="Pick up where you left off">
            <ul className="divide-y divide-white/5">
              {recent.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-white">
                      {p.exam_type} {p.subject} · {p.year} · Paper {p.paper}
                    </p>
                    <p className="text-[11px] text-white/40">
                      {p.original_filename} · {p.stage.replace(/_/g, " ")}
                    </p>
                  </div>
                  <Button onClick={() => open(p.id)}>Open</Button>
                </li>
              ))}
            </ul>
          </Panel>
        )}
      </div>
    );
  }

  // ── Working on a paper ───────────────────────────────────────────────────
  const s = paper.summary;
  const analysed = paper.questions?.length > 0;
  const running  = !!paper.running;
  const cleared  = !!paper.verification.cleared;

  const assetsOutstanding = (s?.assets_detected || 0) - (s?.assets_extracted || 0);
  const unsolved   = (s?.answerable_parts || 0) - (s?.answers_generated || 0);
  const unreviewed = (s?.answers_generated || 0)
    - ((s?.machine_no_concerns || 0) + (s?.machine_review_recommended || 0) + (s?.machine_likely_issue || 0));

  // The single thing to do now. Ordered the way the pipeline runs, so the first
  // unmet condition is the answer — with the verification gate ahead of the work it
  // blocks, otherwise the buttons sit disabled with no explanation on screen.
  const next = (() => {
    if (!analysed) {
      return { key: "analyze", title: "Analyse the paper",
               detail: "Read the PDF, check it is the paper you filed, and pull out every question.",
               cta: "Analyse Paper", action: () => act("analyze") };
    }
    if (!cleared) {
      return { key: "verify", title: "Confirm the paper's identity",
               detail: "The reading below does not match what you filed. Resolve it before anything else runs." };
    }
    if (assetsOutstanding > 0) {
      return { key: "extract_assets", title: `Extract ${assetsOutstanding} question ${assetsOutstanding === 1 ? "image" : "images"}`,
               detail: "Crop each diagram out of the page so the solver can see what the question refers to.",
               cta: "Extract Images", action: () => act("extract_assets") };
    }
    if (unsolved > 0) {
      return { key: "solve", title: `Generate answers for ${unsolved} ${unsolved === 1 ? "question" : "questions"}`,
               detail: "The slowest and most expensive step. Runs a few questions at a time in the background.",
               cta: "Generate Answers", action: () => act("solve") };
    }
    if (unreviewed > 0) {
      return { key: "review", title: `Run machine QA on ${unreviewed} ${unreviewed === 1 ? "answer" : "answers"}`,
               detail: "A second pass that flags answers worth a closer look. Advisory only — it cannot approve anything.",
               cta: "Run Machine QA", action: () => act("review") };
    }
    if ((s?.awaiting_quality_check || 0) > 0) {
      const n = s.awaiting_quality_check;
      return { key: "review", title: `Quality check ${n} re-answered ${n === 1 ? "question" : "questions"}`,
               detail: "These were answered again after their last check, so nothing has looked at them yet. They cannot be approved until it has.",
               cta: "Run Machine QA", action: () => act("review") };
    }
    if ((s?.human_pending || 0) > 0) {
      return { key: "human", title: `Review ${s.human_pending} ${s.human_pending === 1 ? "question" : "questions"}`,
               detail: "Everything automated is done. Nothing reaches a student until you approve it below." };
    }
    return { key: "build", title: "Everything is approved",
             detail: "Build the JSON and import it, in Review & Import." };
  })();

  // Every top-level question number on the paper. Passing these explicitly is what
  // turns "solve what is outstanding" into "solve everything again" — the solver
  // skips already-answered questions unless it is told which groups to do.
  const allGroups = Array.from(
    new Set((paper.questions || []).filter((q) => q.answerable && !q.unsupported).map((q) => q.group))
  ).filter(Boolean);

  const STEP_BUTTONS = [
    { key: "analyze",        label: analysed ? "Re-Analyse" : "Analyse Paper", enabled: true,
      run: () => act("analyze") },
    { key: "extract_assets", label: "Extract Images", enabled: analysed && cleared,
      run: () => act("extract_assets") },
    { key: "solve",          label: "Generate Answers", enabled: analysed && cleared,
      run: () => act("solve") },
    { key: "review",         label: "Run Machine QA", enabled: !!s?.answers_generated,
      run: () => act("review") },
  ];

  return (
    <div className="space-y-4">
      <RoboAnimations />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StageRail stage={paper.stage} running={running} />
        <Button variant="ghost" onClick={() => { setPaper(null); loadRecent(); }}>← All Papers</Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/25 bg-red-500/[0.07] px-3 py-2.5 text-xs text-red-200">
          {error}
        </div>
      )}
      {paper.last_error && !error && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5 text-xs text-amber-200">
          {paper.last_error}
        </div>
      )}

      {analysed && <SummaryPanel paper={paper} />}

      {paper.verification.status && <VerificationPanel paper={paper} onChanged={setPaper} />}

      <Panel title="Pipeline" subtitle="Steps can be re-run individually at any time.">
        {running ? (
          <div className="relative overflow-hidden rounded-xl border border-[#2e92b0]/30 bg-[#2e92b0]/[0.08] px-4 py-3.5">
            <div className="robo-sweep pointer-events-none absolute inset-0 opacity-40" />
            <div className="relative flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2e92b0] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#2e92b0]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-white">{RUNNING_LABEL[paper.stage]}…</p>
                  {paper.progress && (
                    <p className="text-xs tabular-nums text-white/70">
                      <span className="font-semibold text-white">{paper.progress.done}</span>
                      {" of "}{paper.progress.total} {paper.progress.label}
                    </p>
                  )}
                </div>

                {/* Only drawn once there is a real number behind it. A bar that sits at
                    zero for two minutes reads as broken rather than busy. */}
                {paper.progress && (
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-[#2e92b0] transition-[width] duration-500"
                      style={{
                        width: `${Math.min(100, Math.round((paper.progress.done / paper.progress.total) * 100))}%`,
                      }}
                    />
                  </div>
                )}

                <p className="mt-1.5 text-xs text-white/50">
                  Running on this machine. You can close the tab, but keep the laptop awake
                  and online — sleeping or dropping wifi ends the run.
                </p>
              </div>
            </div>
          </div>
        ) : next ? (
          <div className="rounded-xl border border-[#2e92b0]/30 bg-[#2e92b0]/[0.08] px-4 py-3.5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#7fd4ec]">Next step</p>
                <p className="mt-0.5 text-sm font-semibold text-white">{next.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-white/55">{next.detail}</p>
              </div>
              {next.action && (
                <Button variant="primary" onClick={next.action}>{next.cta}</Button>
              )}
            </div>
          </div>
        ) : null}

        {/* Every step stays reachable — the highlight above says which one is due, it
            does not take the others away. Re-running an earlier step is how a bad
            parse gets corrected. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {STEP_BUTTONS.map(({ key, label, run, enabled }) => {
            const isNext = next?.key === key;
            return (
              <Button
                key={key}
                variant={isNext ? "primary" : "secondary"}
                disabled={running || !enabled}
                onClick={() => run()}
                className={isNext ? "" : "opacity-70"}
              >
                {label}
              </Button>
            );
          })}

          {!running && s?.answers_generated > 0 && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(
                  `Re-solve every question under the current parser rules?\n\n` +
                  `${s.answers_generated} answers across ${allGroups.length} question(s) will be ` +
                  `regenerated, discarding ${s.human_approved} approval(s). ` +
                  `Images and question structure are untouched.`
                )) act("solve", { groups: allGroups });
              }}
              className="ml-auto rounded-md px-2 py-1 text-xs text-red-300/70 transition-colors hover:bg-red-500/10 hover:text-red-300"
              title="Discard every answer and generate them again under the current parser rules"
            >
              Re-solve All
            </button>
          )}
        </div>
      </Panel>

      {analysed && (
        <AssetsPanel
          highlight={next?.key === "extract_assets"}
          autoOpen={next?.key === "extract_assets"}
          paper={paper}
          onChanged={setPaper}
          onExtractOnly={(assetIds) => act("extract_assets", { asset_ids: assetIds })}
          onRefineAll={() => act("refine_crops")}
          onReload={reload}
          refreshing={refreshing}
          extracting={running}
        />
      )}

      {s?.answers_generated > 0 && (
        <ReviewPanel
          highlight={next?.key === "human"}
          autoOpen={next?.key === "human"}
          paper={paper}
          onChanged={setPaper}
          running={running}
          // Two separate actions rather than one chained pair. Re-answering and
          // re-checking are different decisions: after editing an answer by hand you
          // want the check without regenerating, and after a retry you may want to
          // read the new answer before spending on QA.
          //
          // The group travels for CONTEXT (parts share the parent's stem) while
          // `numbers` limits what is actually rewritten — otherwise retrying one part
          // of a nine-part question discards eight siblings and their approvals.
          onResolveQuestion={(number, group) =>
            act("solve", { groups: [group], numbers: [number] })
          }
          onCheckQuestion={(number) => act("review", { numbers: [number] })}
          // Bulk versions of the same two actions. The groups are derived from the
          // selected numbers so parts still travel with their siblings for context,
          // while `numbers` limits what is actually rewritten.
          onResolveMany={(numbers) =>
            act("solve", {
              groups: Array.from(new Set(numbers.map((n) => String(n).match(/^\d+/)?.[0]).filter(Boolean))),
              numbers,
            })
          }
          onCheckMany={(numbers) => act("review", { numbers })}
        />
      )}

      {(paper.flags || []).length > 0 && (
        <Panel
          collapsible
          // Open only when something needs a decision. Most flags are routine — an
          // un-run stage reports every question as "answer missing" — so opening by
          // default buries the few that matter under dozens that do not.
          // Always shut on load, and never opened by the pipeline moving on. Flags are
          // reference material you consult, not a step that wants your attention.
          defaultOpen={false}
          title={`Flags — ${paper.flags.length}`}
          subtitle={[
            s.flags_critical ? `${s.flags_critical} critical` : null,
            s.flags_warning  ? `${s.flags_warning} warning`   : null,
            s.flags_info     ? `${s.flags_info} info`         : null,
          ].filter(Boolean).join(" · ")}
        >
          <ul className="divide-y divide-white/5">
            {paper.flags.map((flag, i) => <FlagRow key={i} flag={flag} />)}
          </ul>
        </Panel>
      )}

      {analysed && <FinalizePanel highlight={next?.key === "build"} paper={paper} onChanged={setPaper} />}
    </div>
  );
};

export default PdfPipeline;
